/**
 * Pure helpers for the Analysis tab's Lichess opening-explorer and Syzygy
 * tablebase panels.
 */

/** Total men on the board (both kings included) — the Lichess tablebase
 * covers positions with at most 7. */
export function countPiecesInFen(fen: string | undefined | null): number {
  if (!fen) {
    return 32;
  }
  const placement = fen.split(' ')[0] || '';
  return (placement.match(/[a-z]/gi) || []).length;
}

export const TABLEBASE_MAX_PIECES = 7;

export type WdlPercents = {white: number; draws: number; black: number};

/** Integer percentages that always sum to 100 (largest-remainder rounding),
 * so the stacked bar never shows a seam or overflows. */
export function wdlPercents(
  white: number,
  draws: number,
  black: number,
): WdlPercents {
  const total = white + draws + black;
  if (total <= 0) {
    return {white: 0, draws: 0, black: 0};
  }
  const raw = [
    (white / total) * 100,
    (draws / total) * 100,
    (black / total) * 100,
  ];
  const floors = raw.map(Math.floor);
  let remainder = 100 - floors.reduce((a, b) => a + b, 0);
  // Distribute leftover points to the largest fractional parts.
  const order = raw
    .map((value, index) => ({index, frac: value - floors[index]}))
    .sort((a, b) => b.frac - a.frac);
  for (const {index} of order) {
    if (remainder <= 0) {
      break;
    }
    floors[index] += 1;
    remainder -= 1;
  }
  return {white: floors[0], draws: floors[1], black: floors[2]};
}

/** Compact game counts, matching lichess's own explorer ("1.2M", "84K"). */
export function formatGameCount(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(count >= 10_000_000 ? 0 : 1)}M`;
  }
  if (count >= 10_000) {
    return `${Math.round(count / 1000)}K`;
  }
  return count.toLocaleString();
}

/* ── Tablebase ─────────────────────────────────────────────────────── */

/** Category of a position, from the point of view of its side to move —
 * exactly the vocabulary tablebase.lichess.ovh uses. */
export type TablebaseCategory =
  | 'win'
  | 'cursed-win'
  | 'maybe-win'
  | 'draw'
  | 'blessed-loss'
  | 'maybe-loss'
  | 'loss'
  | 'unknown';

export type TablebaseMove = {
  san: string;
  uci: string;
  category: TablebaseCategory;
  dtz: number | null;
  dtm: number | null;
  checkmate?: boolean;
  stalemate?: boolean;
  zeroing?: boolean;
  [key: string]: any;
};

/**
 * The API reports each move's category from the perspective of the side to
 * move *after* it (the opponent). Flip it to the mover's outcome: a move
 * leaving the opponent 'loss' is the mover's 'win'; 'blessed-loss' (saved
 * by the 50-move rule) flips to 'cursed-win'; draws stay draws.
 */
export function moverOutcome(
  afterCategory: TablebaseCategory,
): TablebaseCategory {
  switch (afterCategory) {
    case 'loss':
      return 'win';
    case 'maybe-loss':
      return 'maybe-win';
    case 'blessed-loss':
      return 'cursed-win';
    case 'win':
      return 'loss';
    case 'maybe-win':
      return 'maybe-loss';
    case 'cursed-win':
      return 'blessed-loss';
    default:
      return afterCategory;
  }
}

export type TablebaseGroups = {
  winning: TablebaseMove[];
  drawing: TablebaseMove[];
  losing: TablebaseMove[];
  unknown: TablebaseMove[];
};

/**
 * Buckets moves by the mover's outcome and orders each bucket the way
 * lichess does: wins fastest-first (checkmates, then lowest distance),
 * losses slowest-first (drag it out), draws zeroing-first.
 */
export function groupTablebaseMoves(
  moves: TablebaseMove[] | undefined | null,
): TablebaseGroups {
  const groups: TablebaseGroups = {
    winning: [],
    drawing: [],
    losing: [],
    unknown: [],
  };
  for (const move of moves || []) {
    const outcome = moverOutcome(move.category);
    if (
      outcome === 'win' ||
      outcome === 'cursed-win' ||
      outcome === 'maybe-win'
    ) {
      groups.winning.push(move);
    } else if (outcome === 'draw') {
      groups.drawing.push(move);
    } else if (
      outcome === 'loss' ||
      outcome === 'blessed-loss' ||
      outcome === 'maybe-loss'
    ) {
      groups.losing.push(move);
    } else {
      groups.unknown.push(move);
    }
  }

  const distance = (m: TablebaseMove) =>
    Math.abs(m.dtm ?? m.dtz ?? Number.MAX_SAFE_INTEGER);

  groups.winning.sort((a, b) => {
    if (!!a.checkmate !== !!b.checkmate) {
      return a.checkmate ? -1 : 1;
    }
    return distance(a) - distance(b);
  });
  groups.losing.sort((a, b) => distance(b) - distance(a));
  groups.drawing.sort((a, b) => Number(!!b.zeroing) - Number(!!a.zeroing));

  return groups;
}
